import { HStack, Icon, Text } from "@chakra-ui/react"
import { FaArrowDownShortWide, FaArrowUpShortWide } from "react-icons/fa6"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"

/**
 * A directional up/down/flat trend as returned by the unified `getReport`
 * query: a signed `delta` over the preceding window, an optional percentage
 * `percent`, and a `direction` discriminator. Passing this via the `trend` prop
 * switches {@link TrendIndicator} into its "report" presentation (signed delta
 * with unit + percentage, e.g. `↑ +12.4 kWh (+8.1%)`), as opposed to the
 * headline/figure `change`-percentage presentation.
 */
/** The direction discriminators the report presentation recognises. */
export type TrendDirection = "up" | "down" | "flat"

export interface TrendDatum {
  /** Signed change over the preceding window, in the KPI's own unit. */
  delta: number
  /** Optional percentage change; omitted from the label when null/undefined. */
  percent?: number | null
  /**
   * Direction discriminator driving the arrow + colour. Typed as `string`
   * (not the `TrendDirection` union) so a `getReport` KPI trend — whose
   * `direction` is a plain `string` in the generated GraphQL type — is directly
   * assignable. Unrecognised values fall back to the "flat" presentation.
   */
  direction: string
}

export interface TrendIndicatorProps {
  /** Percentage change value (headline/figure presentation). */
  change?: number
  /** Override the direction — derived from change if not provided */
  direction?: "increase" | "decrease" | "neutral"
  /**
   * Which direction should be colored green? `"neutral"` colours every change
   * grey. Defaults to `"decrease"` (e.g. for emissions) in the percentage
   * presentation and to `"increase"` in the report presentation.
   */
  goodDirection?: "increase" | "decrease" | "neutral"
  /** Optional tooltip text to explain what the trend is comparing against. */
  tooltipText?: string
  /**
   * Report-style trend (signed delta + optional percent + up/down/flat
   * direction). When provided, the component renders the report presentation
   * and ignores `change` / `direction`. The arrow follows the direction; the
   * colour follows `goodDirection` (green when the change is good, red when it
   * is bad, grey when flat or neutral).
   */
  trend?: TrendDatum | null
  /** Unit appended to the signed delta in the report presentation (e.g. "kWh"). */
  unit?: string | null
}

const HIGH_THRESHOLD = 1000

/** Per-direction glyph for the report presentation. */
const REPORT_TREND_ARROW: Record<TrendDirection, string> = {
  up: "↑",
  down: "↓",
  flat: "→",
}

type TrendTone = "good" | "bad" | "neutral"

/** Colour for whether a report trend is good news, bad news or neither. */
const REPORT_TREND_COLOR: Record<TrendTone, string> = {
  good: "#2f855a",
  bad: "#c53030",
  neutral: "#545454",
}

/** Whether a move in `direction` is good news, given which way is good for the KPI. */
function reportTrendTone(
  direction: TrendDirection,
  goodDirection: NonNullable<TrendIndicatorProps["goodDirection"]>,
): TrendTone {
  if (direction === "flat" || goodDirection === "neutral") return "neutral"
  return (direction === "up") === (goodDirection === "increase") ? "good" : "bad"
}

/** Narrow an arbitrary direction string to a known {@link TrendDirection}, defaulting to "flat". */
function resolveTrendDirection(direction: string): TrendDirection {
  return direction === "up" || direction === "down" ? direction : "flat"
}

/** Round a signed number to one decimal place, matching the KPI value shaping. */
function round1Signed(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * Resolve the trend direction for the *display*, treating a delta that rounds to
 * 0 at one-decimal display precision as flat. The backend `direction` can read
 * "up" for a negligible positive delta (e.g. +0.0001), which would render an
 * up-arrow next to a "+0" delta ("↑ 0 kg/kWh"); collapsing a display-rounded-zero
 * delta to "flat" keeps the arrow/colour/aria-label honest with the shown value.
 */
function resolveDisplayDirection(trend: TrendDatum): TrendDirection {
  if (round1Signed(trend.delta) === 0) return "flat"
  return resolveTrendDirection(trend.direction)
}

/**
 * Format the *numeric* portion of a report-style trend badge (the text that sits
 * beside the coloured arrow). The arrow glyph itself is rendered separately (as
 * its own coloured element) by {@link ReportTrend}, so this returns only the
 * value string.
 *
 * Two presentations, keyed off the KPI unit:
 *  - Percentage-point KPIs (`unit === "pp"`): a "percent change of a percentage"
 *    is confusing, so these keep the signed *absolute* pp delta — e.g.
 *    `+13.9 pp`. A delta rounding to flat/0 reads `0 pp`.
 *  - Value / ratio KPIs (kWh, L, g/kWh, t, unitless): show the signed *relative*
 *    percent only — e.g. `+8.1%` — and drop the absolute delta entirely. A trend
 *    rounding to flat reads `0%`.
 *
 * Callers guarantee `formatReportValue` is only invoked when there is something
 * to show: for value KPIs, {@link ReportTrend} returns null up front when
 * `trend.percent` is null (undefined relative change), so `percent` is non-null
 * here for the non-pp branch.
 */
function formatReportValue(trend: TrendDatum, unit?: string | null): string {
  const isFlat = resolveDisplayDirection(trend) === "flat"
  const isPercentagePoint = unit === "pp"

  if (isPercentagePoint) {
    const delta = isFlat ? 0 : round1Signed(trend.delta)
    const sign = delta > 0 ? "+" : ""
    return `${sign}${delta} pp`
  }

  // Value / ratio KPI: show the relative percent only.
  const percent = isFlat ? 0 : round1Signed(trend.percent!)
  const sign = percent > 0 ? "+" : ""
  return `${sign}${percent}%`
}

/**
 * Report-style presentation: an up/down/flat arrow rendered as its own coloured
 * element, followed by the numeric value alongside it on a single line. The
 * arrow shows which way the value moved; the colour shows whether that is good
 * news for this KPI (see {@link reportTrendTone}), so a rise in generator
 * runtime reads red while a rise in savings reads green.
 *
 * Branching (see {@link formatReportValue}):
 *  - `unit === "pp"` → `{arrow} {signed-delta} pp` (never hidden; pp KPIs don't
 *    use `percent`).
 *  - otherwise → `{arrow} {signed-percent}%`, but renders nothing when
 *    `trend.percent` is null (undefined relative change with the absolute delta
 *    dropped — there's nothing meaningful to show). A genuine flat trend has
 *    `percent === 0`, so it still renders `→ 0%`.
 *
 * A delta that rounds to 0 at display precision reads as flat regardless of the
 * backend `direction` (see {@link resolveDisplayDirection}).
 */
function ReportTrend({
  trend,
  unit,
  goodDirection,
}: {
  trend: TrendDatum
  unit?: string | null
  goodDirection: NonNullable<TrendIndicatorProps["goodDirection"]>
}) {
  const isPercentagePoint = unit === "pp"

  // Value/ratio KPIs with an undefined relative change (percent null) show
  // nothing — the absolute delta is no longer displayed for these. pp KPIs are
  // unaffected (they never use percent).
  if (!isPercentagePoint && trend.percent == null) return null

  const displayDirection = resolveDisplayDirection(trend)
  const arrow = REPORT_TREND_ARROW[displayDirection]
  const tone = reportTrendTone(displayDirection, goodDirection)
  const color = REPORT_TREND_COLOR[tone]
  return (
    <HStack
      as="span"
      gap="2px"
      alignItems="center"
      fontSize="13px"
      fontWeight="600"
      color={color}
      aria-label={`Trend ${displayDirection}`}
      data-trend-tone={tone}
    >
      <Text as="span" color={color} aria-hidden data-trend-arrow={displayDirection}>
        {arrow}
      </Text>
      <Text as="span" color={color}>
        {formatReportValue(trend, unit)}
      </Text>
    </HStack>
  )
}

function TrendIndicator({
  change,
  direction,
  goodDirection,
  tooltipText = "Compared to previous period",
  trend,
  unit,
}: TrendIndicatorProps) {
  // Report presentation: when a `trend` is supplied, render the signed
  // delta/percent form and ignore the `change`-percentage path. A null trend
  // renders nothing (consumers show no arrow when there's no comparable data).
  if (trend !== undefined) {
    if (trend == null) return null
    return <ReportTrend trend={trend} unit={unit} goodDirection={goodDirection ?? "increase"} />
  }

  const resolvedDirection =
    direction ?? (change && change > 0 ? "increase" : change && change < 0 ? "decrease" : "neutral")

  if (resolvedDirection === "neutral" || change === undefined || change === 0) {
    return null
  }

  const isGood = resolvedDirection === (goodDirection ?? "decrease")
  const isNeutral = goodDirection === "neutral"
  const color = isNeutral ? "#545454" : isGood ? "green" : "red"
  
  const ArrowIcon = resolvedDirection === "increase" ? FaArrowUpShortWide : FaArrowDownShortWide
  const absChange = Math.abs(change)
  const exceedsThreshold = !Number.isFinite(change) || absChange > HIGH_THRESHOLD
  const displayValue = exceedsThreshold ? `>${HIGH_THRESHOLD}` : `${absChange}`

  const indicator = (
    <HStack gap={1} alignItems="center">
      <Icon as={ArrowIcon} color={color} boxSize="14px" data-icon={`${resolvedDirection}-${color}`} />
      <Text fontSize="12px" fontWeight="500" color={color}>
        {displayValue}%
      </Text>
    </HStack>
  )

  return (
    <Tooltip content={tooltipText}>
      {indicator}
    </Tooltip>
  )
}

export default TrendIndicator
